import anchorme from "anchorme";
import moment from "moment";

import { Button, Icon, Label, List, Ref, Comment as SemanticComment } from "semantic-ui-react";

import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import ImageAvatar from "../../../ui/components/ImageAvatar/ImageAvatar";
import themeColors from "../../../ui/constants/themeColors";
import useInViewport from "../../../ui/hooks/useInViewport";

import { useProfile } from "../../../authentication/state/profile";

import CommentDropdownMenu from "./components/CommentDropdownMenu";
import CommentForm from "./components/CommentForm";
import CommentUnseen from "./components/CommentUnseen";
import EditCommentForm from "./components/EditCommentForm";

export default function Comment({ sortedComments, onSetNeedsAttention, onComment, comment, onClickReply, disabled }) {
	const { t } = useTranslation();
	const currentUser = useProfile();
	const ref = useRef();
	const inViewport = useInViewport(ref);

	const [editComment, setEditComment] = useState(false);
	const [reply, setReply] = useState(false);
	const [showReplies, setShowReplies] = useState(true);

	const replies = sortedComments.filter((reply) => reply.replyTo === comment.id);

	const sender = comment.sender || comment.user;

	const showDropdown = sender.id === currentUser.id;

	const onReply = async (text) => {
		setReply(false);
		await onComment({ text, replyTo: comment.replyTo || comment.id });
	};

	return (
		<Ref innerRef={ref}>
			<SemanticComment>
				<ImageAvatar src={sender.img} style={{ borderRadius: "50%", float: "left", width: 40 }} />
				{comment.ref && <CommentUnseen comment={comment} inViewport={inViewport} />}

				<SemanticComment.Content>
					<SemanticComment.Author style={{ display: "inline" }}>{sender.name}</SemanticComment.Author>

					<SemanticComment.Metadata>
						{moment(comment.created?.toDate()).fromNow()}

						{comment.edited && <i style={{ marginLeft: 2 }}>({t("edited", "Edited").toLowerCase()})</i>}
					</SemanticComment.Metadata>

					{onSetNeedsAttention && (
						<Icon
							name="fire"
							style={{ marginLeft: 2, cursor: "pointer", fontSize: 12 }}
							color={comment.needsAttention ? "orange" : null}
							onClick={() => onSetNeedsAttention(comment)}
						/>
					)}

					{showDropdown && !disabled && (
						<CommentDropdownMenu
							setEditComment={setEditComment}
							editComment={editComment}
							comment={comment}
						/>
					)}

					<SemanticComment.Text>
						{!editComment ? (
							<div
								dangerouslySetInnerHTML={{
									__html: anchorme(comment.text || "", {
										truncate: [26, 15],
										attributes: [
											function (urlObj) {
												if (urlObj.protocol !== "mailto:")
													return { name: "target", value: "blank" };
											},
										],
									}),
								}}
							/>
						) : (
							<EditCommentForm comment={comment} onClose={() => setEditComment(false)} />
						)}

						{comment.attachments?.length > 0 && (
							<List style={{ marginTop: 5 }}>
								{comment.attachments.map((attachment, index) => (
									<Label
										size="tiny"
										basic
										key={index}
										as="a"
										href={attachment.url}
										target="_blank"
										rel="noreferrer"
										download
									>
										<Icon name="file" />

										{attachment.name}
									</Label>
								))}
							</List>
						)}
					</SemanticComment.Text>

					{!editComment && !disabled && (
						<SemanticComment.Actions>
							<SemanticComment.Action
								style={{ marginRight: 5 }}
								onClick={onClickReply ? onClickReply : () => setReply(true)}
							>
								{t("answer", "Reply")}
							</SemanticComment.Action>

							{replies.length > 0 && (
								<Button
									icon
									circular
									style={{
										fontSize: 10,
										lineHeight: "12px",
										padding: "1px 7px",
										color: themeColors.GREY,
										backgroundColor: "var(--color-bg-input)",
										border: `1px solid ${themeColors.BORDER}`,
									}}
									onClick={() => setShowReplies((value) => !value)}
								>
									<Icon link fitted name={showReplies ? "chevron up" : "chevron down"} />
								</Button>
							)}
						</SemanticComment.Actions>
					)}
				</SemanticComment.Content>

				{replies.length > 0 && showReplies && (
					<SemanticComment.Group>
						{replies.map((reply, index) => (
							<Comment
								key={index}
								disabled={disabled}
								sortedComments={sortedComments}
								onComment={onComment}
								comment={reply}
								onClickReply={() => setReply(true)}
								onSetNeedsAttention={onSetNeedsAttention}
							/>
						))}
					</SemanticComment.Group>
				)}

				{reply && (
					<CommentForm
						confirmationButtonText={t("reply", "Reply")}
						onClickCancel={() => setReply(false)}
						onSave={onReply}
						isReplying
					/>
				)}
			</SemanticComment>
		</Ref>
	);
}
